/**
 * A group's lists for one board row in the order they are SENT today
 * (19/10/2026): the first time each list's drops go out (×N when a list is
 * sent several times), then lists whose every drop is off — skipped by hand
 * or stopped by a verdict, as the Live card tells them apart — then the lists
 * of today's set with no drop at all ("not planned"). `dropListSequence` is
 * the board cell's own reading (26/10/2026, user: "he wants to see all the
 * list multiple times based on the used time … L1 L2 L3 L1 L2 L3"): every
 * live drop in send order, its list each time it is used.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DayPlanGroupRow } from '@trafficpulse/shared';

import type { DayPlanContainerRow, DayPlanDropRow } from '@/features/drop-generator/drop-generator.types';
import { type ClockCtx, dkLabel } from '@/features/drop-generator/utils/clock/dk-label';
import { minutesLabel } from '@/features/drop-generator/utils/format';

/** Why none of a list's drops goes out today. */
export type TodayListOff = 'skipped' | 'stopped';

export interface TodayListEntry {
  wiserId: number;
  name: string | null;
  /** Recipient-local minute of its first live drop today; null = none goes out. */
  minute: number | null;
  /** Live drops to this list today. */
  sends: number;
  /** Every drop to it today is off; null = it goes out, or nothing is planned. */
  off: TodayListOff | null;
}

type ListRef = { wiserId: number; name: string | null };
type GroupToday = Pick<DayPlanGroupRow, 'listGroupId' | 'listsToday' | 'listOfDay'>;
type Names = Map<number, string | null>;

const byTime = (a: DayPlanDropRow, b: DayPlanDropRow) => a.minute - b.minute || a.atUtc.localeCompare(b.atUtc);
const offOf = (drop: DayPlanDropRow): TodayListOff => (drop.runtime?.skipReason?.startsWith('stopped') ? 'stopped' : 'skipped');
const entryOf = (wiserId: number, name: string | null, off: TodayListOff | null = null): TodayListEntry => ({ wiserId, name, minute: null, sends: 0, off });
// A fan-out drop may come without its list's name: today's set knows it.
const nameOf = (drop: DayPlanDropRow, names: Names) => drop.listName ?? names.get(drop.listWiserId as number) ?? null;

function addLive(out: Map<number, TodayListEntry>, drops: DayPlanDropRow[], names: Names): void {
  for (const drop of drops) {
    if (drop.runtime?.tombstoned) continue;
    const id = drop.listWiserId as number;
    const entry = out.get(id) ?? { ...entryOf(id, nameOf(drop, names)), minute: drop.minute };
    out.set(id, { ...entry, sends: entry.sends + 1 });
  }
}

/** Lists none of whose drops goes out — "stopped" when a verdict stopped any of them. */
function addOff(out: Map<number, TodayListEntry>, drops: DayPlanDropRow[], names: Names): void {
  for (const drop of drops) {
    const id = drop.listWiserId as number;
    const prev = out.get(id);
    if (!prev) out.set(id, entryOf(id, nameOf(drop, names), offOf(drop)));
    else if (prev.off === 'skipped' && offOf(drop) === 'stopped') out.set(id, { ...prev, off: 'stopped' });
  }
}

export function todayListOrder(preview: DayPlanContainerRow | undefined, group: GroupToday): TodayListEntry[] {
  const drops = (preview?.drops ?? []).filter((d) => d.groupId === group.listGroupId && d.listWiserId !== null).sort(byTime);
  // No drop at all (nothing planned yet): the list of the day still reads, at the end.
  const set: ListRef[] = group.listsToday ?? (drops.length || !group.listOfDay ? [] : [group.listOfDay]);
  const names: Names = new Map(set.map((list) => [list.wiserId, list.name]));
  const out = new Map<number, TodayListEntry>();
  addLive(out, drops, names);
  addOff(out, drops, names);
  for (const list of set) if (!out.has(list.wiserId)) out.set(list.wiserId, entryOf(list.wiserId, list.name));
  return [...out.values()];
}

/** One live drop of the group on the board cell: when it goes and to which list. */
export interface DropListLine {
  key: string;
  minute: number;
  /** Breaks a tie between two groups' drops at the same minute, as the planner orders them. */
  atUtc: string;
  wiserId: number;
  name: string | null;
  /** ISO instant the drop went out (26/10/2026 — the line's green ✓); null while it waits. */
  sentAt: string | null;
}

/** Every live drop of the group in send order — a list repeats each time it is used. */
export function dropListSequence(preview: DayPlanContainerRow | undefined, group: GroupToday): DropListLine[] {
  const set: ListRef[] = group.listsToday ?? (group.listOfDay ? [group.listOfDay] : []);
  const names: Names = new Map(set.map((list) => [list.wiserId, list.name]));
  return (preview?.drops ?? [])
    .filter((d) => d.groupId === group.listGroupId && d.listWiserId !== null && !d.runtime?.tombstoned)
    .sort(byTime)
    // A slot fans out to several groups: the group keeps the key unique once the board merges them.
    .map((d, i) => ({ key: `${d.groupId}-${d.slotId}-${i}`, minute: d.minute, atUtc: d.atUtc, wiserId: d.listWiserId as number, name: nameOf(d, names), sentAt: d.runtime?.sentAt ?? null }));
}

/** "05:00 🇩🇰" — Danish first; the country's time with its flag when the timezone is unknown. */
export function sendTimeLabel(minute: number, clock: ClockCtx): string {
  const dk = dkLabel(minute, clock.tz, clock.runDate);
  return dk ? `${dk} 🇩🇰` : `${clock.flag} ${minutesLabel(minute)}`;
}

export const listName = (list: ListRef): string => list.name ?? `#${list.wiserId}`;
